import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, PencilIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import { ecosystemService, type Ecosystem } from '../services/ecosystemService';
import { Button } from '../components/ui/Button';
import { TextField } from '../components/ui/Field';
import { Surface } from '../components/ui/Surface';

const STATE_BADGE: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  published: 'bg-green-100 text-green-700 border-green-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200',
};

const Ecosystems: React.FC = () => {
  const navigate = useNavigate();
  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await ecosystemService.list({ search: search || undefined, limit: 100 });
      const items = (res as any)?.items ?? (res as any)?.ecosystems ?? (Array.isArray(res) ? res : []);
      setEcosystems(items);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load ecosystems');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [search]);

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this ecosystem? This cannot be undone.')) return;
    setDeleting(true);
    setDeleteId(id);
    try {
      await ecosystemService.delete(id);
      toast.success('Ecosystem deleted');
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-agentic-white-off via-agentic-primary-blue/5 to-agentic-accent-purple/10">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-white/30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-agentic-text-primary via-agentic-primary-blue to-agentic-accent-purple bg-clip-text text-transparent">
                Ecosystems
              </h1>
              <p className="mt-1 text-agentic-text-secondary">
                3-layer agentic workflows — entry agents, orchestrator, functional agents
              </p>
            </div>
            <Button tone="primary-fade"
              onClick={() => navigate('/ecosystems/new/config')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              New Ecosystem
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <div className="mb-6">
          <TextField
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ecosystems..."
            className="w-full max-w-sm px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <ArrowPathIcon className="h-8 w-8 animate-spin text-agentic-primary-blue" />
          </div>
        )}

        {/* Empty state */}
        {!loading && ecosystems.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-lg">No ecosystems found.</p>
            <Button tone="primary-fade"
              onClick={() => navigate('/ecosystems/new/config')}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-medium"
            >
              Create your first ecosystem
            </Button>
          </div>
        )}

        {/* Grid */}
        {!loading && ecosystems.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
            {ecosystems.map((eco) => (
              <Surface tone="card-hover"
                key={eco.id}
                className="rounded-xl border transition-shadow p-5 flex flex-col gap-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">{eco.name}</h3>
                    {eco.description && (
                      <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{eco.description}</p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 text-xs font-medium px-2 py-0.5 rounded-full border ${STATE_BADGE[eco.state] || STATE_BADGE.draft}`}
                  >
                    {eco.state}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <span>
                    {eco.entryAgentMappings?.length ?? 0} entry agent{eco.entryAgentMappings?.length !== 1 ? 's' : ''}
                  </span>
                  <span>
                    {eco.functionalAgentMappings?.length ?? 0} functional agent{eco.functionalAgentMappings?.length !== 1 ? 's' : ''}
                  </span>
                  <span className={`ml-auto ${eco.isActive ? 'text-green-600' : 'text-gray-400'}`}>
                    {eco.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                  <Button tone="custom"
                    onClick={() => navigate(`/ecosystems/${eco.id}/config`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-agentic-primary-blue border border-agentic-primary-blue rounded-lg hover:bg-blue-50 transition-colors"
                  >
                    <PencilIcon className="h-3.5 w-3.5" />
                    Configure
                  </Button>
                  <Button tone="custom"
                    onClick={() => handleDelete(eco.id)}
                    disabled={deleting && deleteId === eco.id}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-red-500 border border-red-300 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                    {deleting && deleteId === eco.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </Surface>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Ecosystems;
